import { Stack, Group, Text, TextInput, Switch, Table, Badge, Code } from '@mantine/core';
import { IconDeviceDesktop } from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, useDeferredValue, type KeyboardEvent } from 'react';
import type { PalvelutunnusStats } from '@taksikortti/shared';
import { usePalvelutunnukset } from '../api/palvelutunnukset';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { TableSkeleton } from '../components/TableSkeleton';
import { t } from '../lib/strings';
import { formatDateTime } from '../lib/dates';

export function PalvelutunnuksetPage() {
  const navigate = useNavigate({ from: '/palvelutunnukset' });
  const search = useSearch({ from: '/palvelutunnukset' });

  const [q, setQ] = useState(search.q ?? '');
  const deferredQ = useDeferredValue(q);

  const repeatOnly = search.repeatOnly ?? true;
  const { data, isLoading, isError, error, refetch } = usePalvelutunnukset({
    q: deferredQ || undefined,
    minCases: repeatOnly ? 2 : 1,
  });

  // URL writes use replace — typing must not stack history entries (back-button trap).
  function updateSearch(patch: Partial<typeof search>): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }
  // Debounced q → URL (same pattern as the worklist search).
  useEffect(() => {
    const h = setTimeout(() => {
      if ((search.q ?? '') !== q) updateSearch({ q: q || undefined });
    }, 300);
    return () => clearTimeout(h);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  function openTerminal(palvelutunnus: string): void {
    void navigate({ to: '/cases', search: { q: palvelutunnus, showDone: true } });
  }

  return (
    <Stack gap="md">
      <PageHeader title={t.palvelutunnukset.title} subtitle={t.palvelutunnukset.subtitle} />

      <Group gap="md" align="end">
        <TextInput
          label={t.palvelutunnukset.filters.search}
          placeholder={t.palvelutunnukset.filters.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          w={360}
        />
        <Switch
          label={t.palvelutunnukset.filters.repeatOnly}
          checked={repeatOnly}
          onChange={(e) => updateSearch({ repeatOnly: e.currentTarget.checked })}
        />
      </Group>

      {isLoading && <TableSkeleton rows={8} />}
      {isError && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && data.rows.length === 0 && (
        <EmptyState
          icon={<IconDeviceDesktop size={28} />}
          title={deferredQ ? t.palvelutunnukset.emptyNoMatches : t.palvelutunnukset.empty}
        />
      )}

      {data && data.rows.length > 0 && (
        <>
          <Text size="sm" c="dimmed">{t.palvelutunnukset.count(data.rows.length)}</Text>
          <Table.ScrollContainer minWidth={640}>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t.palvelutunnukset.columns.palvelutunnus}</Table.Th>
                  <Table.Th w={120} ta="right">{t.palvelutunnukset.columns.incidents}</Table.Th>
                  <Table.Th w={170} ta="right">{t.palvelutunnukset.columns.cards}</Table.Th>
                  <Table.Th w={160}>{t.palvelutunnukset.columns.lastCase}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.rows.map((row) => (
                  <PalvelutunnusRow key={row.palvelutunnus} row={row} onOpen={() => openTerminal(row.palvelutunnus)} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}
    </Stack>
  );
}

function PalvelutunnusRow({ row, onOpen }: { row: PalvelutunnusStats; onOpen: () => void }) {
  const heat = row.totalCases >= 5 ? 'red' : row.totalCases >= 3 ? 'orange' : 'gray';
  return (
    <Table.Tr
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      onClick={onOpen}
      onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <Table.Td><Code>{row.palvelutunnus}</Code></Table.Td>
      <Table.Td ta="right">
        <Badge color={heat} variant="light" aria-label={`${t.palvelutunnukset.columns.incidents}: ${row.totalCases}`}>
          {row.totalCases}
        </Badge>
      </Table.Td>
      <Table.Td ta="right">{row.distinctCards}</Table.Td>
      <Table.Td>{formatDateTime(row.lastCaseAt)}</Table.Td>
    </Table.Tr>
  );
}
